// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  Building2,
  Headset,
  Scale,
  TrendingUp,
} from 'lucide-react';
import {
  Briefcase,
  Car,
  ConciergeBell,
  GraduationCap,
  Landmark,
  Plane,
  ShoppingBag,
  Stethoscope,
  Truck,
  UtensilsCrossed,
} from '../../builder-components/agentIcons';
import { getPersonaTypes, getVoices, getLanguages, getDefaultCompletionTypes } from '@/service/aiAgentApi';
import { listKnowledgeBases, getKnowledgeBaseStatus } from '@/service/knowledgeBaseApi';

/* ------------------------------------------------------------ personas */
const PERSONA_RULES = [
  { k: ['real estate', 'realestate', 'real_estate', 'property', 'realty', 'housing'], icon: Building2, tag: 'Listings, site visits and leads' },
  { k: ['hospitality', 'hotel', 'resort', 'lodging', 'front desk'], icon: ConciergeBell, tag: 'Bookings, stays and guests' },
  { k: ['restaurant', 'food', 'dining', 'cafe', 'catering'], icon: UtensilsCrossed, tag: 'Orders, tables and menus' },
  { k: ['health', 'clinic', 'hospital', 'medical', 'pharma'], icon: Stethoscope, tag: 'Appointments and patient care' },
  { k: ['education', 'school', 'college', 'university', 'edtech', 'coaching'], icon: GraduationCap, tag: 'Admissions and student queries' },
  { k: ['bank', 'finance', 'insurance', 'loan', 'fintech'], icon: Landmark, tag: 'Accounts, loans and claims' },
  { k: ['retail', 'ecommerce', 'e-commerce', 'shop', 'store'], icon: ShoppingBag, tag: 'Orders, returns and stock' },
  { k: ['travel', 'tour', 'airline', 'flight'], icon: Plane, tag: 'Itineraries and bookings' },
  { k: ['auto', 'car', 'vehicle', 'dealership'], icon: Car, tag: 'Test drives and service' },
  { k: ['logistics', 'delivery', 'transport', 'courier'], icon: Truck, tag: 'Shipments and tracking' },
  { k: ['legal', 'law', 'advocate'], icon: Scale, tag: 'Consultations and case intake' },
  { k: ['sales', 'marketing', 'lead'], icon: TrendingUp, tag: 'Lead qualification and follow-up' },
  { k: ['support', 'helpdesk', 'help desk', 'customer service', 'call center', 'call centre'], icon: Headset, tag: 'Tickets and customer queries' },
];
const DEFAULT_PERSONA = { icon: Briefcase, tag: 'General purpose voice agent' };

export function personaStyle(p) {
  const hay = `${p?.name || ''} ${p?.type || ''}`.toLowerCase();
  const rule = PERSONA_RULES.find((r) => r.k.some((k) => hay.includes(k)));
  return rule || DEFAULT_PERSONA;
}

/* -------------------------------------------------------------- voices */
export function normalizeVoice(v) {
  const raw = String(v?.gender ?? v?.voice_gender ?? v?.sex ?? '').trim().toLowerCase();
  const gender = raw.startsWith('f') ? 'female' : raw.startsWith('m') ? 'male' : '';
  const url =
    v?.voice_url ?? v?.sample_url ?? v?.sample_audio_url ?? v?.sample_audio ?? v?.audio_url ?? v?.preview_url ?? v?.sample ?? '';
  return { ...v, gender, sample_url: typeof url === 'string' ? url : '' };
}

/* ----------------------------------------------------- knowledge bases */
const pick = (obj, keys, fallback = null) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== '') return obj[key];
  }
  return fallback;
};
export const kbId = (row) => pick(row, ['id', 'knowledge_base_id', 'kb_id'], '');
export const kbName = (row) => pick(row, ['name', 'kb_name'], 'Untitled');
const kbStatus = (row) => pick(row, ['overall_status', 'status', 'ingestion_status'], null);
const READY = new Set(['completed', 'ready', 'success']);
export const kbReady = (row) => READY.has(String(kbStatus(row) || '').toLowerCase());
export const kbFiles = (row) => pick(row, ['file_count', 'files_count', 'document_count', 'total_files'], null);


function useList(load, map = (x) => x) {
  const [state, set] = useState({ items: [], loading: true, error: '' });
  useEffect(() => {
    let off = false;
    load()
      .then((list) => !off && set({ items: Array.isArray(list) ? list.map(map) : [], loading: false, error: '' }))
      .catch((err) => !off && set({ items: [], loading: false, error: typeof err === 'string' ? err : 'Could not load this list.' }));
    return () => {
      off = true;
    };
  }, []);
  return state;
}

export function useStudioData() {
  const voices = useList(getVoices, normalizeVoice);
  const personas = useList(getPersonaTypes);
  const languages = useList(getLanguages);
  const defaultCompletions = useList(getDefaultCompletionTypes, (d) => ({
    id: d?.id,
    name: String(d?.name ?? d?.type ?? '').trim(),
    description: String(d?.description ?? '').trim(),
  }));

  const [kbs, setKbs] = useState({ items: [], loading: true });
  useEffect(() => {
    let off = false;
    listKnowledgeBases()
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : [];
        if (off) return;
        setKbs({ items: list, loading: false });
        list.forEach((row) => {
          const id = kbId(row);
          if (!id) return;
          getKnowledgeBaseStatus(id)
            .then((s) => {
              const next = pick(s, ['overall_status', 'status'], undefined);
              if (next == null || off) return;
              setKbs((prev) => ({
                ...prev,
                items: prev.items.map((r) => (kbId(r) === id ? { ...r, overall_status: next } : r)),
              }));
            })
            .catch(() => {});
        });
      })
      .catch(() => !off && setKbs({ items: [], loading: false }));
    return () => {
      off = true;
    };
  }, []);

  return { voices, personas, languages, defaultCompletions, kbs };
}
